import { Button, Form, Input, Modal, Table, Typography, message } from "antd";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { currentSiteDomain, toPublicHost } from "../core/site-domain";
import { serverFetch } from "../core/server";
import { sessionFromApi } from "../store/session";
import { setUserSession } from "../store/user-slice";
import "./companies-page.css";

const CompaniesPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selecting, setSelecting] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [availability, setAvailability] = useState(null);
  const instanceValue = Form.useWatch("instanceName", form);
  const siteDomain = currentSiteDomain();

  const loadRows = useCallback(() => {
    setLoading(true);
    serverFetch("companies")
      .then(setRows)
      .catch((e) => message.error(e.userMessage || e.message || "Не удалось загрузить компании"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  useEffect(() => {
    const name = instanceValue?.trim().toLowerCase();
    if (!addOpen || !name) {
      setAvailability(null);
      return undefined;
    }

    const timer = setTimeout(() => {
      serverFetch("instances/available", { queryParams: { name } })
        .then(setAvailability)
        .catch(() => setAvailability(null));
    }, 300);

    return () => clearTimeout(timer);
  }, [addOpen, instanceValue]);

  const onSelect = (company) => {
    if (selecting) return;
    setSelecting(true);
    serverFetch("auth/select-company", {
      method: "POST",
      bodyData: { companyId: company.id }
    })
      .then((session) => {
        const sessionId = localStorage.getItem("sessionId");
        dispatch(setUserSession(sessionFromApi(session, sessionId)));
        navigate("/cabinet");
      })
      .catch((e) => message.error(e.userMessage || e.message || "Не удалось выбрать компанию"))
      .finally(() => setSelecting(false));
  };

  const openAdd = () => {
    form.resetFields();
    setAvailability(null);
    setAddOpen(true);
  };

  const onAdd = (values) => {
    setSaving(true);
    serverFetch("companies", {
      method: "POST",
      bodyData: {
        name: values.name.trim(),
        instanceName: values.instanceName.trim()
      }
    })
      .then(() => {
        message.success("Компания добавлена");
        setAddOpen(false);
        loadRows();
      })
      .catch((e) => message.error(e.userMessage || e.message || "Не удалось добавить компанию"))
      .finally(() => setSaving(false));
  };

  const instanceHint = () => {
    if (!availability) return "Имя копии: папка и сервис будут проверены и приведены к рабочему виду.";
    if (!availability.ok) return availability.reason;
    const notes = [];
    if (availability.unitExists) notes.push("сервис есть — будет перезапущен");
    if (availability.folderExists && availability.dataGdbExists)
      notes.push("папка есть, data.gdb сохранится, остальное заменится");
    else if (availability.folderExists) notes.push("папка есть и будет заменена");
    if (!notes.length) notes.push("имя свободно, будет создана новая копия");
    return notes.join(". ");
  };

  const columns = [
    { title: "Компания", dataIndex: "name", key: "name" },
    { title: "Домен", dataIndex: "domain", key: "domain", render: (value) => toPublicHost(value) || "—" }
  ];

  return (
    <div className="companies">
      <Typography.Title level={2}>Компании</Typography.Title>
      <Typography.Paragraph type="secondary">
        Суперадминистратор видит все компании. Нажмите на строку, чтобы открыть кабинет.
      </Typography.Paragraph>
      <div className="companies-toolbar">
        <Button type="primary" onClick={openAdd}>
          Добавить
        </Button>
      </div>
      <Table
        className="companies-table"
        rowKey="id"
        loading={loading || selecting}
        columns={columns}
        dataSource={rows}
        pagination={false}
        onRow={(row) => ({
          onClick: () => onSelect(row)
        })}
      />
      <Modal
        title="Новая компания"
        open={addOpen}
        onCancel={() => setAddOpen(false)}
        onOk={() => form.submit()}
        okText="Добавить"
        cancelText="Отмена"
        confirmLoading={saving}
        okButtonProps={{ disabled: availability && !availability.ok }}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" onFinish={onAdd}>
          <Form.Item
            name="name"
            label="Название"
            rules={[{ required: true, message: "Укажите название компании" }]}
          >
            <Input placeholder="Название компании" />
          </Form.Item>
          <Form.Item
            name="instanceName"
            label="Поддомен"
            extra={instanceHint()}
            validateStatus={availability && !availability.ok ? "error" : undefined}
            rules={[{ required: true, message: "Укажите поддомен" }]}
          >
            <Input addonBefore="https://" addonAfter={`.${siteDomain}`} placeholder="company" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CompaniesPage;
